import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MessageCircle, Bug, Sparkles, Star, Check, Download, RefreshCw, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_COLOR = {
  low: 'bg-slate-200 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-700',
};

export default function FeedbackAdminTab({ headers, language }) {
  const tt = (en, es) => (language === 'es' ? es : en);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterSev, setFilterSev] = useState('');
  const [showUnread, setShowUnread] = useState(false);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterType) p.set('type', filterType);
      if (filterSev) p.set('severity', filterSev);
      if (showUnread) p.set('unread', 'true');
      p.set('limit', '100');
      const r = await axios.get(`${API}/admin/feedback?${p}`, { headers });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setUnread(r.data.unread || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterType, filterSev, showUnread]);

  const openDetail = (f) => {
    setDetail(f);
    setNote(f.admin_note || '');
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/feedback/${id}/mark-read`, {}, { headers });
      toast.success(tt('Marked as read', 'Marcado como leído'));
      load();
    } catch { toast.error('Failed'); }
  };

  const saveNote = async () => {
    if (!detail) return;
    try {
      await axios.post(`${API}/admin/feedback/${detail.id}/note`, { note }, { headers });
      toast.success(tt('Note saved', 'Nota guardada'));
      setDetail(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const download = () => {
    // Backend serves CSV — hit with auth header via axios blob
    axios.get(`${API}/admin/feedback/export.csv`, { headers, responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = 'wakacasa-feedback.csv';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Failed'));
  };

  const deleteFeedback = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/feedback/${confirmDelete.id}`, { headers });
      toast.success(tt('Feedback deleted', 'Comentario eliminado'));
      setConfirmDelete(null);
      // If the detail dialog is showing the same row, close it too
      if (detail?.id === confirmDelete.id) setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Failed to delete', 'Error al eliminar'));
    } finally {
      setDeleting(false);
    }
  };

  const typeIcon = (t) => t === 'bug' ? <Bug className="w-3.5 h-3.5" />
    : t === 'prompt' ? <Sparkles className="w-3.5 h-3.5" />
    : <MessageCircle className="w-3.5 h-3.5" />;

  return (
    <div className="space-y-4" data-testid="feedback-admin-tab">
      {/* Header + stats */}
      <Card className="border-2 border-[#D4AF37]/30 bg-gradient-to-br from-amber-50/30 to-white">
        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-2xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-[#0B192C]" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <p className="font-heading text-lg font-semibold text-[#0B192C]">
              {tt('User Feedback', 'Comentarios')}
              <Badge className="ml-2 bg-red-100 text-red-700 border border-red-300 text-[10px]">
                {unread} {tt('unread', 'sin leer')}
              </Badge>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {total} {tt('total submissions', 'envíos totales')}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} className="h-9" data-testid="feedback-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />{tt('Refresh', 'Actualizar')}</Button>
          <Button size="sm" onClick={download} className="h-9 bg-[#0B192C] text-[#D4AF37] hover:bg-[#142944]" data-testid="feedback-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-2 items-center">
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="h-9 border border-slate-200 rounded-md bg-white px-2 text-sm" data-testid="feedback-filter-type">
            <option value="">{tt('All types', 'Todos los tipos')}</option>
            <option value="feedback">{tt('Feedback', 'Comentarios')}</option>
            <option value="bug">{tt('Bug', 'Errores')}</option>
            <option value="prompt">{tt('Prompt answers', 'Respuestas de prompt')}</option>
          </select>
          <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
            className="h-9 border border-slate-200 rounded-md bg-white px-2 text-sm" data-testid="feedback-filter-sev">
            <option value="">{tt('All severity', 'Toda gravedad')}</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showUnread} onChange={e => setShowUnread(e.target.checked)} data-testid="feedback-filter-unread" />
            {tt('Unread only', 'Solo sin leer')}
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2"><Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tt('When', 'Cuándo')}</TableHead>
                    <TableHead>{tt('Type', 'Tipo')}</TableHead>
                    <TableHead>{tt('User', 'Usuario')}</TableHead>
                    <TableHead>{tt('Rating / Severity', 'Puntuación / Gravedad')}</TableHead>
                    <TableHead>{tt('Comment', 'Comentario')}</TableHead>
                    <TableHead className="text-right">{tt('Actions', 'Acciones')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">{tt('No feedback yet', 'Aún no hay comentarios')}</TableCell></TableRow>
                  ) : items.map((f, i) => (
                    <TableRow key={f.id} className={f.is_read ? '' : 'bg-amber-50/30'} data-testid={`feedback-row-${i}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(f.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700 text-[10px] gap-1 capitalize">
                          {typeIcon(f.type)}{f.type}
                        </Badge>
                        {f.trigger && <div className="text-[10px] text-slate-400 mt-0.5">{f.trigger}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{f.user_email}</div>
                        <div className="text-[10px] text-slate-400 capitalize">{f.user_role}</div>
                      </TableCell>
                      <TableCell>
                        {f.rating != null && (
                          <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-slate-300'}`} />)}
                          </div>
                        )}
                        {f.severity && <Badge className={`${SEV_COLOR[f.severity]} text-[10px] capitalize`}>{f.severity}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[300px] truncate" title={f.comment}>{f.comment || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openDetail(f)} data-testid={`feedback-view-${i}`}><Eye className="w-3.5 h-3.5" /></Button>
                        {!f.is_read && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 hover:bg-emerald-50" onClick={() => markRead(f.id)} data-testid={`feedback-markread-${i}`}><Check className="w-3.5 h-3.5" /></Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(f)} data-testid={`feedback-delete-${i}`} aria-label={tt('Delete', 'Eliminar')}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="feedback-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {typeIcon(detail.type)} {detail.type}
                  {detail.trigger && <Badge className="bg-slate-100 text-slate-700 text-[10px]">{detail.trigger}</Badge>}
                  {detail.severity && <Badge className={`${SEV_COLOR[detail.severity]} text-[10px] capitalize`}>{detail.severity}</Badge>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div><span className="text-xs text-slate-500">{tt('From', 'De')}:</span> {detail.user_email} ({detail.user_role})</div>
                <div><span className="text-xs text-slate-500">{tt('URL', 'URL')}:</span> <a href={detail.url} className="text-[#D4AF37] hover:underline">{detail.url}</a></div>
                <div><span className="text-xs text-slate-500">{tt('When', 'Cuándo')}:</span> {new Date(detail.created_at).toLocaleString()}</div>
                {detail.rating != null && (
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= detail.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-slate-300'}`} />)}
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 mb-1">{tt('Comment', 'Comentario')}</p>
                  <p className="whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">{detail.comment || <span className="text-slate-400">—</span>}</p>
                </div>
                {detail.meta && Object.keys(detail.meta).length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-500">{tt('Auto-captured metadata', 'Metadatos capturados')}</summary>
                    <pre className="bg-slate-50 border border-slate-200 rounded-md p-2 mt-1 overflow-auto max-h-40 text-[10px]">{JSON.stringify(detail.meta, null, 2)}</pre>
                  </details>
                )}
                <div>
                  <p className="text-xs text-slate-500 mb-1">{tt('Admin note', 'Nota de admin')}</p>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} data-testid="feedback-admin-note" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDetail(null)}>{tt('Close', 'Cerrar')}</Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(detail)}
                  className="border-red-300 text-red-600 hover:bg-red-50 gap-1.5"
                  data-testid="feedback-detail-delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />{tt('Delete', 'Eliminar')}
                </Button>
                <Button onClick={saveNote} className="bg-[#D4AF37] text-[#0B192C] font-semibold" data-testid="feedback-save-note">
                  {tt('Save note & mark read', 'Guardar y marcar leído')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v && !deleting) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="feedback-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              {tt('Delete this feedback?', '¿Eliminar este comentario?')}
            </DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="text-sm space-y-2">
              <p className="text-slate-600">
                {tt(
                  'This will permanently remove the feedback entry. This action cannot be undone.',
                  'Esto eliminará el comentario de forma permanente. Esta acción no se puede deshacer.'
                )}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
                <div><span className="text-slate-500">{tt('From', 'De')}:</span> {confirmDelete.user_email} <span className="text-slate-400">({confirmDelete.user_role})</span></div>
                <div><span className="text-slate-500">{tt('Type', 'Tipo')}:</span> <span className="capitalize">{confirmDelete.type}</span>{confirmDelete.severity ? ` · ${confirmDelete.severity}` : ''}</div>
                {confirmDelete.comment && (
                  <div className="text-slate-600 line-clamp-3 pt-1">&ldquo;{confirmDelete.comment}&rdquo;</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              {tt('Cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={deleteFeedback}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="feedback-delete-confirm"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {deleting ? tt('Deleting…', 'Eliminando…') : tt('Delete', 'Eliminar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
